import { getPriceHistory } from '@simplywallst/services';
import { startOfMonth, subDays, subYears } from 'date-fns';
import type { QueryFunctionContext } from '@tanstack/react-query';

interface PriceQueryData {
  date: number | null;
  price: number | null;
}

export interface PriceData {
  priceData: number[][];
  priceMin: number;
  priceMax: number;
  dayZero: number;
}

export const getDefault = (): PriceData => ({
  priceData: [],
  priceMin: 0,
  priceMax: 0,
  dayZero: 0,
});

export function getQueryKey(lookup: string): [string, string] {
  return ['price-history', lookup];
}
const priceDataReducer = (data: PriceQueryData[] = []): PriceData => {
  const [priceList, priceData] = data.reduce<[number[], number[][]]>(
    ([l, d], b) => {
      if (b.date === null || b.price === null) return [l, d];
      const list = [...l, b.price];
      const data = [...d, [b.date, b.price]];
      return [list, data];
    },
    [[], []]
  );
  const priceMin = Math.min(...priceList);
  const priceMax = Math.max(...priceList);
  const dayZero =
    priceData.length > 0
      ? priceData[priceData.length - 1][0]
      : new Date().getTime();
  return {
    priceData,
    priceMin,
    priceMax,
    dayZero,
  };
};

export const queryPriceData = async ({
  queryKey: [_key, canonicalUrl],
}: QueryFunctionContext<[string, string]>): Promise<PriceData> => {
  const twoYearsAgo = subYears(new Date(), 2);
  const firstOfMonth = startOfMonth(twoYearsAgo).getTime();
  //fetch prices from one week earlier to be able to get a valid price in case
  //the 1st of the month 2y ago is undefined (e.g. it was Saturday).
  const oneWeekEarlier = subDays(firstOfMonth, 7).getTime();
  const res = await getPriceHistory({
    companyId: canonicalUrl,
    payload: {
      start_timestamp: oneWeekEarlier,
    },
  });
  const { data } = res;
  if (data) {
    const priceList = data.data
      .map((n) => ({
        date: n.date,
        price: n.close,
      }))
      .reverse();
    return {
      ...priceDataReducer(priceList),
    };
  }
  return getDefault();
};
